import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, getDocs, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface Contact {
  id: string;
  userId: string;
  contactId: string;
  contactName: string;
  contactPhoto?: string;
  lastMessage?: {
    content: string;
    timestamp: Date;
    senderId: string;
  };
  unreadCount: number;
  isOnline: boolean;
  createdAt: Date;
}

interface ContactsContextType {
  contacts: Contact[];
  addContact: (userId: string) => Promise<void>;
  removeContact: (contactId: string) => Promise<void>;
  isContact: (userId: string) => Promise<boolean>;
}

const ContactsContext = createContext<ContactsContextType | null>(null);

export function ContactsProvider({ children }: { children: React.ReactNode }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setContacts([]);
      return;
    }

    const contactsRef = collection(db, 'contacts');

    // Query para contatos onde o usuário é o dono
    const userContactsQuery = query(
      contactsRef,
      where('userId', '==', currentUser.uid)
    );

    // Query para contatos onde o usuário é o contato
    const contactOfQuery = query(
      contactsRef,
      where('contactId', '==', currentUser.uid)
    );

    // Função para processar um documento de contato
    const processContactDoc = async (docSnapshot: any) => {
      try {
        const contactData = docSnapshot.data();
        const otherUserId = contactData.userId === currentUser.uid 
          ? contactData.contactId 
          : contactData.userId;
        
        const userDocRef = doc(db, 'users', otherUserId);
        const userDocSnap = await getDoc(userDocRef);
        
        if (!userDocSnap.exists()) {
          return null;
        }

        const userData = userDocSnap.data();
        return {
          id: docSnapshot.id,
          userId: contactData.userId,
          contactId: otherUserId,
          contactName: userData.displayName || userData.email || 'Usuário',
          contactPhoto: userData.photoBase64,
          lastMessage: contactData.lastMessage ? {
            content: contactData.lastMessage.content,
            timestamp: new Date(contactData.lastMessage.timestamp.seconds * 1000),
            senderId: contactData.lastMessage.senderId
          } : undefined,
          unreadCount: contactData.unreadCount || 0,
          isOnline: userData.isOnline || false,
          createdAt: contactData.createdAt ? new Date(contactData.createdAt.seconds * 1000) : new Date()
        };
      } catch (error) {
        console.error('Erro crítico ao processar contato:', error);
        return null;
      }
    };

    // Configurar os listeners
    const unsubscribeUserContacts = onSnapshot(userContactsQuery, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          processContactDoc(change.doc).then(contact => {
            if (contact) {
              setContacts(prev => {
                const filtered = prev.filter(c => c.id !== contact.id);
                return [...filtered, contact];
              });
            }
          });
        } else if (change.type === 'removed') {
          setContacts(prev => prev.filter(c => c.id !== change.doc.id));
        }
      });
    });

    const unsubscribeContactOf = onSnapshot(contactOfQuery, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          processContactDoc(change.doc).then(contact => {
            if (contact) {
              setContacts(prev => {
                const filtered = prev.filter(c => c.id !== contact.id);
                return [...filtered, contact];
              });
            }
          });
        } else if (change.type === 'removed') {
          setContacts(prev => prev.filter(c => c.id !== change.doc.id));
        }
      });
    });

    return () => {
      unsubscribeUserContacts();
      unsubscribeContactOf();
    };
  }, [currentUser]);

  // Monitorar status online dos contatos
  useEffect(() => {
    if (!currentUser || contacts.length === 0) return;

    const contactIds = contacts.map(contact => contact.contactId);
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('id', 'in', contactIds));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updates: Partial<Contact>[] = [];
      
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        updates.push({
          contactId: userData.id,
          isOnline: userData.isOnline || false,
          contactName: userData.displayName || userData.email,
          contactPhoto: userData.photoBase64
        });
      });

      setContacts(prevContacts => 
        prevContacts.map(contact => {
          const update = updates.find(u => u.contactId === contact.contactId);
          if (update) {
            return { ...contact, ...update };
          }
          return contact;
        })
      );
    });

    return () => unsubscribe();
  }, [currentUser, contacts.length]);

  const addContact = async (contactUserId: string) => {
    if (!currentUser) {
      console.log('Não há usuário logado para adicionar contato');
      return;
    }

    try {
      // Verificar se já é contato em ambas as direções
      const contactsRef = collection(db, 'contacts');
      const q1 = query(
        contactsRef,
        where('userId', '==', currentUser.uid),
        where('contactId', '==', contactUserId)
      );
      const q2 = query(
        contactsRef,
        where('userId', '==', contactUserId),
        where('contactId', '==', currentUser.uid)
      );
      
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);

      if (!snapshot1.empty || !snapshot2.empty) {
        console.log('Usuário já é um contato');
        return;
      }

      // Buscar informações do usuário a ser adicionado
      const userDoc = await getDoc(doc(db, 'users', contactUserId));
      
      if (!userDoc.exists()) {
        console.error('Usuário não encontrado:', contactUserId);
        return;
      }

      const userData = userDoc.data();

      // Buscar informações do usuário atual
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (!currentUserDoc.exists()) {
        console.error('Dados do usuário atual não encontrados');
        return;
      }

      const currentUserData = currentUserDoc.data();

      // Preparar dados dos contatos
      const contactData1 = {
        userId: currentUser.uid,
        contactId: contactUserId,
        contactName: userData.displayName || userData.email,
        contactPhoto: userData.photoBase64,
        unreadCount: 0,
        createdAt: serverTimestamp(),
        lastMessage: null
      };

      const contactData2 = {
        userId: contactUserId,
        contactId: currentUser.uid,
        contactName: currentUserData.displayName || currentUserData.email,
        contactPhoto: currentUserData.photoBase64,
        unreadCount: 0,
        createdAt: serverTimestamp(),
        lastMessage: null
      };

      // Adicionar ambos os documentos em paralelo
      const [doc1, doc2] = await Promise.all([
        addDoc(collection(db, 'contacts'), contactData1),
        addDoc(collection(db, 'contacts'), contactData2)
      ]);

      console.log('Contatos adicionados com sucesso:', {
        doc1: doc1.id,
        doc2: doc2.id
      });

    } catch (error) {
      console.error('Erro ao adicionar contato:', error);
      throw error;
    }
  };

  const removeContact = async (contactUserId: string) => {
    if (!currentUser) return;

    try {
      // Buscar o documento do contato onde o usuário atual é o dono ou é o contato
      const contactsRef = collection(db, 'contacts');
      const q1 = query(
        contactsRef,
        where('userId', '==', currentUser.uid),
        where('contactId', '==', contactUserId)
      );
      const q2 = query(
        contactsRef,
        where('userId', '==', contactUserId),
        where('contactId', '==', currentUser.uid)
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);

      // Deletar todos os documentos encontrados
      const deletePromises = [
        ...snapshot1.docs,
        ...snapshot2.docs
      ].map(doc => deleteDoc(doc.ref));

      await Promise.all(deletePromises);
      console.log('Contato removido com sucesso');
    } catch (error) {
      console.error('Erro ao remover contato:', error);
      throw error;
    }
  };

  const isContact = async (userId: string) => {
    if (!currentUser) return false;

    try {
      // Verificar diretamente no Firestore
      const contactsRef = collection(db, 'contacts');
      const q = query(
        contactsRef,
        where('userId', '==', currentUser.uid),
        where('contactId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Erro ao verificar contato:', error);
      return false;
    }
  };

  const value = {
    contacts,
    addContact,
    removeContact,
    isContact
  };

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
}

export function useContacts() {
  const context = useContext(ContactsContext);
  if (!context) {
    throw new Error('useContacts must be used within a ContactsProvider');
  }
  return context;
}
